'use client';

import { useEffect } from 'react';

import { useRouter } from 'next/navigation';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  registerPushDeviceToken,
  unregisterPushDeviceToken,
} from '~/(tenant)/_lib/server/push-token-server-actions';

import {
  type CapacitorNativePlatform,
  getNativeOsVersion,
} from './capacitor-device-info';

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface PushNotificationsPlugin {
  checkPermissions: () => Promise<{ receive: string }>;
  requestPermissions: () => Promise<{ receive: string }>;
  register: () => Promise<void>;
  unregister: () => Promise<void>;
  addListener: (
    event:
      | 'registration'
      | 'registrationError'
      | 'pushNotificationActionPerformed',
    handler: (payload: never) => void,
  ) => PluginListenerHandle | Promise<PluginListenerHandle>;
}

interface AppPlugin {
  getInfo?: () => Promise<{ id?: string } | null>;
}

interface CapacitorGlobal {
  Plugins?: { PushNotifications?: PushNotificationsPlugin; App?: AppPlugin };
  isNativePlatform?: () => boolean;
  isPluginAvailable?: (name: string) => boolean;
  getPlatform?: () => string;
}

interface RegistrationToken {
  value: string;
}

interface NotificationAction {
  notification?: { data?: Record<string, unknown> };
}

// Module-scoped: permission check + register() must run once per app
// session, not once per React mount (HMR, layout remounts).
let pushSetupStarted = false;
let pushSetupUserId: string | null = null;

const PUSH_TOKEN_STORAGE_KEY = 'capacitor-push-device-token';

/**
 * Native push bootstrap for the Capacitor shells:
 *
 *  - waits for an authenticated session, then asks for notification
 *    permission and registers with APNs/FCM
 *  - forwards the resulting device token to the server (upsert keyed on
 *    token; re-running on every launch doubles as the last_seen refresh)
 *  - routes notification taps through the Next.js router using the payload
 *    `url` key — the same path-only routing as CapacitorDeepLinkHandler
 *
 * Guarded by isPluginAvailable: app-store builds that predate the push
 * plugin run this same remote JS bundle and must silently no-op. No-op on
 * the web too — browsers never see the OS prompt.
 */
export function CapacitorPushNotificationsHandler() {
  const router = useRouter();
  const supabase = useSupabase();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor as CapacitorGlobal | undefined;

    if (!cap?.isNativePlatform?.()) return;
    if (!cap.isPluginAvailable?.('PushNotifications')) return;

    const push = cap.Plugins?.PushNotifications;
    if (!push) return;

    let currentToken: string | null = null;
    let signOutInProgress = false;
    let pendingRegistration = Promise.resolve();

    try {
      currentToken = window.localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    } catch {
      // Private browsing/storage restrictions must not block registration.
    }

    const forgetCurrentToken = () => {
      currentToken = null;

      try {
        window.localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
      } catch {
        // The in-memory token has still been cleared for this app session.
      }
    };

    const routeFromPayload = (raw: unknown) => {
      if (typeof raw !== 'string' || raw.length === 0) return;
      try {
        if (raw.startsWith('/')) {
          router.push(raw);
          return;
        }
        // Absolute URLs route by path only — the webview is already on the
        // tenant origin; never swap it.
        const url = new URL(raw);
        router.push(`${url.pathname}${url.search}${url.hash}`);
      } catch {
        // Malformed target — ignore rather than crash the shell.
      }
    };

    const handles: {
      cancelled: boolean;
      removers: Array<() => Promise<void> | void>;
    } = { cancelled: false, removers: [] };

    const addListener = (
      event: Parameters<PushNotificationsPlugin['addListener']>[0],
      handler: (payload: never) => void,
    ) => {
      Promise.resolve(push.addListener(event, handler))
        .then((listener) => {
          if (handles.cancelled) {
            void listener.remove();
          } else {
            handles.removers.push(listener.remove);
          }
        })
        .catch(() => {
          // Older plugin versions can throw here; safe to ignore.
        });
    };

    const registerToken = async (token: string) => {
      // A native registration callback can arrive after Supabase has already
      // emitted SIGNED_OUT. Never retain or bind that stale callback for the
      // next account.
      if (signOutInProgress || !pushSetupUserId) return;

      currentToken = token;

      try {
        window.localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
      } catch {
        // The in-memory value still covers sign-out during this app session.
      }

      const info = await cap.Plugins?.App?.getInfo?.();
      const platform: CapacitorNativePlatform =
        cap.getPlatform?.() === 'android' ? 'android' : 'ios';

      const result = await registerPushDeviceToken({
        token,
        platform,
        bundleId: info?.id ?? 'unknown',
        osVersion: getNativeOsVersion(navigator.userAgent, platform),
      });

      if (!result.success) {
        throw new Error(result.error);
      }
    };

    const queueTokenRegistration = (token: string) => {
      const registration = pendingRegistration.then(() => registerToken(token));

      pendingRegistration = registration.catch(() => {
        // Registration is retried on the next launch; never surface this.
      });

      return registration;
    };

    addListener('registration', (token: RegistrationToken) => {
      void queueTokenRegistration(token.value).catch(() => {
        // Registration is retried on the next launch; never surface this.
      });
    });

    addListener('registrationError', () => {
      // Denied entitlement / simulator / network — nothing actionable here.
    });

    // Tap routing is attached unconditionally (before auth): a cold-start
    // tap must route even if the session check below is still in flight.
    addListener(
      'pushNotificationActionPerformed',
      (action: NotificationAction) => {
        routeFromPayload(action.notification?.data?.url);
      },
    );

    const trySetup = async (knownUserId?: string) => {
      const userId =
        knownUserId ?? (await supabase.auth.getSession()).data.session?.user.id;

      if (!userId) return;
      if (pushSetupStarted && pushSetupUserId === userId) return;

      if (pushSetupUserId !== userId) {
        pushSetupStarted = false;
      }

      pushSetupStarted = true;
      pushSetupUserId = userId;

      try {
        // Rebind a token retained across WebView reloads or account switches
        // before asking APNs/FCM to emit it again.
        if (currentToken) {
          await queueTokenRegistration(currentToken);
        }

        const status = await push.checkPermissions();

        if (status.receive === 'prompt') {
          const requested = await push.requestPermissions();
          if (requested.receive !== 'granted') return;
        } else if (status.receive !== 'granted') {
          return;
        }

        await push.register();
      } catch {
        // Allow a retry on the next launch.
        pushSetupStarted = false;
        pushSetupUserId = null;
      }
    };

    void trySetup();

    const originalSignOut = supabase.auth.signOut;
    const wrappedSignOut: typeof originalSignOut = async (...args) => {
      if (args[0]?.scope === 'others') {
        return originalSignOut.apply(supabase.auth, args);
      }

      signOutInProgress = true;

      try {
        await pendingRegistration;

        if (currentToken) {
          try {
            const result = await unregisterPushDeviceToken({
              token: currentToken,
            });

            if (!result.success) {
              throw new Error(result.error);
            }
          } catch {
            // The OS unregister below is the privacy fallback when server-side
            // token cleanup is unavailable.
          }
        }

        try {
          // Remove this app's APNs/FCM registration before auth is cleared.
          // The next SIGNED_IN setup calls register() again.
          await push.unregister();
        } catch {
          // Signing out must still proceed if native cleanup is unavailable.
        }

        forgetCurrentToken();

        return await originalSignOut.apply(supabase.auth, args);
      } finally {
        pushSetupStarted = false;
        pushSetupUserId = null;
        signOutInProgress = false;
      }
    };

    supabase.auth.signOut = wrappedSignOut;

    const { data: authSub } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT') {
          pushSetupStarted = false;
          pushSetupUserId = null;

          if (!signOutInProgress) {
            // Session expiry and remote revocation bypass the signOut wrapper,
            // so at least stop native delivery and discard the stale token.
            forgetCurrentToken();
            void push.unregister().catch(() => undefined);
          }

          return;
        }

        if (event === 'SIGNED_IN' && session) {
          if (pushSetupUserId !== session.user.id) {
            pushSetupStarted = false;
          }

          void trySetup(session.user.id);
        }
      },
    );

    return () => {
      handles.cancelled = true;
      for (const remove of handles.removers) void remove();
      authSub.subscription.unsubscribe();

      if (supabase.auth.signOut === wrappedSignOut) {
        supabase.auth.signOut = originalSignOut;
      }
    };
  }, [router, supabase]);

  return null;
}
